import { useState, useMemo } from 'react';
import { Plus, Loader2, Wallet, Pencil, Trash2, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useDepositPolicies,
  useCreateDepositPolicy,
  useUpdateDepositPolicy,
  useDeleteDepositPolicy,
} from '@/hooks/api/useDepositPolicies';
import {
  DepositBasis,
  DepositDueTiming,
  type DepositPolicy,
} from '@pms/shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const BASIS_LABELS: Record<DepositBasis, string> = {
  [DepositBasis.PERCENT_OF_STAY]: 'Percent of stay',
  [DepositBasis.FIXED]: 'Fixed',
  [DepositBasis.FIRST_NIGHT]: 'First night',
};

const BASIS_OPTIONS = Object.values(DepositBasis);

const TIMING_LABELS: Record<DepositDueTiming, string> = {
  [DepositDueTiming.AT_BOOKING]: 'At booking',
  [DepositDueTiming.DAYS_BEFORE_ARRIVAL]: 'Days before arrival',
};

const TIMING_OPTIONS = Object.values(DepositDueTiming);

function basisTakesValue(basis: DepositBasis): boolean {
  return (
    basis === DepositBasis.PERCENT_OF_STAY || basis === DepositBasis.FIXED
  );
}

function fmtAmount(p: DepositPolicy): string {
  if (p.basis === DepositBasis.FIRST_NIGHT) return '--';
  if (p.value === null) return '--';
  const n = parseFloat(p.value);
  if (Number.isNaN(n)) return '--';
  return p.basis === DepositBasis.PERCENT_OF_STAY
    ? `${n.toFixed(2)}%`
    : n.toFixed(2);
}

function fmtDue(p: DepositPolicy): string {
  if (p.dueTiming === DepositDueTiming.AT_BOOKING) return 'At booking';
  return `${p.daysBeforeArrival ?? 0} days before arrival`;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function DepositPoliciesPage() {
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<DepositPolicy | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useDepositPolicies({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteDepositPolicy();

  const allItems: DepositPolicy[] = listQuery.data?.data ?? [];

  const items: DepositPolicy[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
    );
  }, [allItems, search]);

  function handleDelete(item: DepositPolicy) {
    if (!window.confirm('Delete this deposit policy?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        // Server soft-blocks when referenced by a booking / rate plan (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Deposit Policies"
        description="Advance-deposit rules due at booking"
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            disabled={!propertyId}
          >
            <Plus size={16} aria-hidden="true" />
            Add Deposit Policy
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search deposit policies"
        />
      </div>

      {!propertyId ? (
        <EmptyState
          icon={Wallet}
          title="Select a property"
          description="Select a property to continue."
        />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2
            size={32}
            className="animate-spin text-primary-500"
            aria-label="Loading"
          />
        </div>
      ) : listQuery.isError ? (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No deposit policies"
          description="Create your first deposit policy."
          actionLabel="Add Deposit Policy"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Basis</Th>
                  <Th>Amount/%</Th>
                  <Th>Due</Th>
                  <Th>Default</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {p.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {p.code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {BASIS_LABELS[p.basis]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {fmtAmount(p)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {fmtDue(p)}
                    </td>
                    <td className="px-4 py-3">
                      {p.isDefault ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"
                          title="Default policy"
                        >
                          <Star size={14} className="fill-amber-400" />
                          Default
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          p.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(p);
                            setShowForm(true);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                          aria-label="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(p)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditing(null);
        }}
        title={editing ? 'Edit Deposit Policy' : 'Add Deposit Policy'}
        width="max-w-xl"
      >
        <DepositPolicyForm
          propertyId={propertyId}
          editing={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function DepositPolicyForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: DepositPolicy | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [basis, setBasis] = useState<DepositBasis>(
    editing?.basis ?? DepositBasis.PERCENT_OF_STAY,
  );
  const [value, setValue] = useState(
    editing?.value != null ? parseFloat(editing.value).toFixed(2) : '',
  );
  const [dueTiming, setDueTiming] = useState<DepositDueTiming>(
    editing?.dueTiming ?? DepositDueTiming.AT_BOOKING,
  );
  const [daysBeforeArrival, setDaysBeforeArrival] = useState(
    editing?.daysBeforeArrival != null
      ? String(editing.daysBeforeArrival)
      : '',
  );
  const [isDefault, setIsDefault] = useState(editing?.isDefault ?? false);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [fieldError, setFieldError] = useState('');

  const createMutation = useCreateDepositPolicy();
  const updateMutation = useUpdateDepositPolicy();
  const mutationError = createMutation.error ?? updateMutation.error;

  const needsValue = basisTakesValue(basis);
  const needsDays = dueTiming === DepositDueTiming.DAYS_BEFORE_ARRIVAL;

  const isValid =
    name.trim().length > 0 &&
    code.trim().length > 0 &&
    (!needsValue || value.trim().length > 0) &&
    (!needsDays || daysBeforeArrival.trim().length > 0);

  function clearError() {
    setFieldError('');
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    let parsedValue: number | undefined;
    if (needsValue) {
      const n = parseFloat(value);
      if (Number.isNaN(n) || n < 0) {
        setFieldError('Enter a valid value of 0 or more.');
        return;
      }
      if (basis === DepositBasis.PERCENT_OF_STAY && n > 100) {
        setFieldError('Percentage must be 0–100.');
        return;
      }
      parsedValue = Number(n.toFixed(2));
    }

    let parsedDays: number | undefined;
    if (needsDays) {
      const d = Number.parseInt(daysBeforeArrival, 10);
      if (Number.isNaN(d) || d < 0) {
        setFieldError('Enter a valid number of days.');
        return;
      }
      parsedDays = d;
    }

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            name: name.trim(),
            code: code.trim(),
            basis,
            value: parsedValue,
            dueTiming,
            daysBeforeArrival: parsedDays,
            isDefault,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          name: name.trim(),
          code: code.trim(),
          basis,
          value: parsedValue,
          dueTiming,
          daysBeforeArrival: parsedDays,
          isDefault,
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="dp-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="dp-name"
          type="text"
          required
          maxLength={255}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="dp-code"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Code <span className="text-red-500">*</span>
        </label>
        <input
          id="dp-code"
          type="text"
          required
          maxLength={100}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="dp-basis"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Basis <span className="text-red-500">*</span>
        </label>
        <select
          id="dp-basis"
          value={basis}
          onChange={(e) => {
            setBasis(e.target.value as DepositBasis);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {BASIS_OPTIONS.map((b) => (
            <option key={b} value={b}>
              {BASIS_LABELS[b]}
            </option>
          ))}
        </select>
      </div>
      {needsValue && (
        <div>
          <label
            htmlFor="dp-value"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Value <span className="text-red-500">*</span>
            {basis === DepositBasis.PERCENT_OF_STAY
              ? ' (% 0–100)'
              : ' (amount)'}
          </label>
          <input
            id="dp-value"
            type="number"
            min="0"
            step="0.01"
            required
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              clearError();
            }}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}
      <div>
        <label
          htmlFor="dp-timing"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Due timing <span className="text-red-500">*</span>
        </label>
        <select
          id="dp-timing"
          value={dueTiming}
          onChange={(e) => {
            setDueTiming(e.target.value as DepositDueTiming);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {TIMING_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {TIMING_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
      {needsDays && (
        <div>
          <label
            htmlFor="dp-days"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Days before arrival <span className="text-red-500">*</span>
          </label>
          <input
            id="dp-days"
            type="number"
            min="0"
            step="1"
            required
            value={daysBeforeArrival}
            onChange={(e) => {
              setDaysBeforeArrival(e.target.value);
              clearError();
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          id="dp-default"
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label
          htmlFor="dp-default"
          className="text-sm font-medium text-gray-700"
        >
          Set as default
        </label>
      </div>
      <div className="flex items-center gap-2">
        <input
          id="dp-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="dp-active" className="text-sm font-medium text-gray-700">
          Active
        </label>
      </div>

      {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}

      {mutationError && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={createMutation.isPending || updateMutation.isPending}
          disabled={!isValid}
        >
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50"
    >
      {children}
    </th>
  );
}
